import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import BASE_URL from '../../url';
import OnboardingDetailDrawer from '../components/OnboardingDetailDrawer';
import CandidateDetail from '../../Resume_Submission/Components/CandidateDetail';
import RoleDetailView from '../../erprecruitment/Components/RoleDetailView';
import '../components/OnboardingStyles.css';
import { 
  Users, CheckCircle2, Clock, Search, Filter, RefreshCw, 
  ExternalLink, UserCheck, ShieldAlert, FileSpreadsheet, 
  ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, ArrowLeft,
  Building2, ArrowUpDown, ArrowUp, ArrowDown, X, Briefcase
} from 'lucide-react';

const EMPLOYMENT_TYPE_OPTIONS = [
  'W2',
  'C2C',
  '1099',
  'Full-Time',
  'Contract',
  'Contract-to-Hire',
  'Part-Time',
  'Temporary',
  'Internship',
  'Freelance'
];

const normalizeEmploymentType = (val) => {
  if (!val) return '';
  const trimmed = String(val).trim();
  if (trimmed === 'Contract (W2)') return 'W2';
  if (trimmed === 'Contract (C2C)') return 'C2C';
  if (trimmed === 'Contract (1099)') return '1099';
  return trimmed;
};

// Calculate effective total/completed steps (W2 candidates exclude 3 supplier steps, making 11 total instead of 14)
const getEffectiveSteps = (inst) => {
  const isW2 = ['W2', 'W-2', 'CONTRACT (W2)'].includes(String(inst?.EmploymentType || '').trim().toUpperCase());
  const rawTotal = inst?.TotalSteps || 9;
  const rawComp = inst?.CompletedSteps || 0;
  if (isW2 && rawTotal === 14) {
    const effectiveTotal = 11;
    const effectiveComp = Math.min(effectiveTotal, rawComp);
    const percent = Math.min(100, Math.round((effectiveComp / effectiveTotal) * 100));
    return { totalSteps: effectiveTotal, completedSteps: effectiveComp, percent };
  }
  const percent = rawTotal > 0 ? Math.min(100, Math.round((rawComp / rawTotal) * 100)) : 0;
  return { totalSteps: rawTotal, completedSteps: rawComp, percent };
};

const OnboardingQueue = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('IN_PROGRESS');
  const [activeKpiFilter, setActiveKpiFilter] = useState(null);
  const [clientFilter, setClientFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [quickSelect, setQuickSelect] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [updatingEmpTypeId, setUpdatingEmpTypeId] = useState(null);

  const handleEmploymentTypeChange = async (instanceId, newType, currentType, candidateId) => {
    if (newType === currentType) return;

    const result = await Swal.fire({
      title: 'Change Employment Type?',
      text: `Are you sure you want to change the Employment Type to "${newType}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3b5bfd',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, change it',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      // Re-trigger state clone to ensure select resets back to previous value
      setInstances(prev => [...prev]);
      return;
    }

    try {
      setUpdatingEmpTypeId(instanceId);
      // Optimistic update
      setInstances(prev => prev.map(inst => 
        inst.OnboardingInstanceId === instanceId ? { ...inst, EmploymentType: newType } : inst
      ));

      const token = localStorage.getItem('token');
      await axios.put(
        `${BASE_URL}/api/onboarding/instances/${instanceId}/employment-type`,
        { employmentType: newType, candidateId },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      Swal.fire({
        icon: 'success',
        title: 'Updated!',
        text: `Employment Type changed to "${newType}" successfully.`,
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Failed to update employment type:', err);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: err.response?.data?.message || 'Failed to update employment type.'
      });
      fetchQueue();
    } finally {
      setUpdatingEmpTypeId(null);
    }
  };

  // Auto-open drawer if ?instanceId= is passed in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const instId = params.get('instanceId');
    if (instId) {
      setSelectedInstanceId(instId);
    }
  }, [location.search]);

  // Helper to format onboarded date
  const formatOnboardDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (entityFilter) params.entityId = entityFilter;

      const res = await axios.get(`${BASE_URL}/api/onboarding/queue`, { params });
      if (res.data.success) {
        setInstances(res.data.data || []);
      }
    } catch (err) {
      console.error('Error loading onboarding queue:', err);
    } finally {
      setLoading(false);
    }
  }, [entityFilter]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Auto-refresh queue when candidate profile is updated in CandidateDetail modal
  useEffect(() => {
    const handleCandidateUpdated = () => fetchQueue();
    window.addEventListener('candidateListUpdated', handleCandidateUpdated);
    return () => window.removeEventListener('candidateListUpdated', handleCandidateUpdated);
  }, [fetchQueue]);

  // Reset pagination when search, filters, or sorting change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, entityFilter, statusFilter, activeKpiFilter, clientFilter, supplierFilter, startDate, endDate, sortConfig, pageSize]);

  // Extract unique client company names for client filter dropdown
  const clientOptions = Array.from(
    new Set(instances.map(inst => inst.ClientCompanyName).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  // Extract unique contractor/supplier company names for supplier filter dropdown
  const supplierOptions = Array.from(
    new Set(instances.map(inst => inst.ContractorCompanyName).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  // Date utilities for Quick Select presets
  const toYMD = (d) => {
    if (!d) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const calculateDateRange = (preset) => {
    const today = new Date();
    let start = new Date(today);
    let end = new Date(today);

    if (preset === 'All') {
      return { startDate: '', endDate: '' };
    } else if (preset === 'Today') {
      start = new Date(today);
      end = new Date(today);
    } else if (preset === 'Yesterday') {
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      end = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    } else if (preset === 'This Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Monday
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today);
    } else if (preset === 'Last Week') {
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1) - 7;
      start = new Date(today.getFullYear(), today.getMonth(), diff);
      end = new Date(today.getFullYear(), today.getMonth(), diff + 6);
    } else if (preset === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today);
    } else if (preset === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (preset === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today);
    } else if (preset === 'Last Quarter') {
      const quarter = Math.floor(today.getMonth() / 3) - 1;
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    }
    return { startDate: toYMD(start), endDate: toYMD(end) };
  };

  const handleQuickSelectChange = (e) => {
    const val = e.target.value;
    setQuickSelect(val);
    if (val === 'Custom') return;

    if (val === 'All') {
      setStartDate('');
      setEndDate('');
      return;
    }

    const { startDate: newStart, endDate: newEnd } = calculateDateRange(val);
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  // Sort handler for sortable columns (Initiated and Checklist Progress only)
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        if (prev.direction === 'asc') return { key, direction: 'desc' };
        return { key: null, direction: 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  // Helper to determine if candidate placement is Hired (completed onboarding or stage marked HIRED)
  const isCandidateHired = (inst) => {
    return inst.StatusCode === 'COMPLETED' || 
           inst.CurrentStageCode === 'HIRED' || 
           inst.StatusCode === 'HIRED';
  };

  // Filter instances by search query, client, supplier, date range, and KPI card / status
  const effectiveStatusFilter = activeKpiFilter || statusFilter;

  const filteredInstances = instances.filter((inst) => {
    // KPI Card & Status filter
    if (effectiveStatusFilter === 'IN_PROGRESS' && inst.StatusCode !== 'IN_PROGRESS') {
      return false;
    }
    if (effectiveStatusFilter === 'NEAR_COMPLETION' && (inst.StatusCode !== 'IN_PROGRESS' || (inst.CompletedSteps || 0) < 7)) {
      return false;
    }
    if (effectiveStatusFilter === 'COMPLETED' && inst.StatusCode !== 'COMPLETED') {
      return false;
    }
    if (effectiveStatusFilter === 'HIRED' && !isCandidateHired(inst)) {
      return false;
    }
    if (effectiveStatusFilter === 'YET_TO_HIRE' && (inst.StatusCode !== 'IN_PROGRESS' || isCandidateHired(inst))) {
      return false;
    }
    if (clientFilter && (inst.ClientCompanyName || '') !== clientFilter) {
      return false;
    }
    if (supplierFilter && (inst.ContractorCompanyName || '') !== supplierFilter) {
      return false;
    }
    if (startDate || endDate) {
      const dStr = inst.InitiatedAtUtc || inst.CreatedAtUtc;
      if (!dStr) return false;
      const instDate = new Date(dStr);
      if (isNaN(instDate.getTime())) return false;
      const yyyy = instDate.getFullYear();
      const mm = String(instDate.getMonth() + 1).padStart(2, '0');
      const dd = String(instDate.getDate()).padStart(2, '0');
      const instYMD = `${yyyy}-${mm}-${dd}`;

      if (startDate && instYMD < startDate) return false;
      if (endDate && instYMD > endDate) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const candName = `${inst.CandidateFirstName || ''} ${inst.CandidateLastName || ''}`.toLowerCase();
    const jobTitle = (inst.JobTitle || '').toLowerCase();
    const clientName = (inst.ClientCompanyName || '').toLowerCase();
    const instId = String(inst.OnboardingInstanceId);
    const externalRef = (inst.ExternalJobReference || '').toLowerCase();
    return candName.includes(q) || jobTitle.includes(q) || clientName.includes(q) || instId.includes(q) || externalRef.includes(q);
  });

  // Apply column sorting (Initiated and Checklist columns only)
  const sortedInstances = [...filteredInstances].sort((a, b) => {
    if (!sortConfig.key) return 0;
    if (sortConfig.key === 'initiated') {
      const dateA = new Date(a.InitiatedAtUtc || a.CreatedAtUtc || 0).getTime() || 0;
      const dateB = new Date(b.InitiatedAtUtc || b.CreatedAtUtc || 0).getTime() || 0;
      return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    if (sortConfig.key === 'checklist') {
      const statsA = getEffectiveSteps(a);
      const statsB = getEffectiveSteps(b);
      const pctA = statsA.percent;
      const pctB = statsB.percent;

      return sortConfig.direction === 'asc' ? pctA - pctB : pctB - pctA;
    }
    return 0;
  });

  // Pagination Calculations
  const totalItems = sortedInstances.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedInstances = sortedInstances.slice(startIndex, endIndex);

  // Helper for generating page numbers array with dots
  const getPageNumbers = (current, total) => {
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 4) {
      return [1, 2, 3, 4, 5, '...', total];
    }
    if (current >= total - 3) {
      return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, '...', current - 1, current, current + 1, '...', total];
  };

  // KPI Metrics Calculation (across all loaded instances under current entity/filters)
  const totalInProgress = instances.filter(i => i.StatusCode === 'IN_PROGRESS').length;
  const readyToComplete = instances.filter(i => i.StatusCode === 'IN_PROGRESS' && (i.CompletedSteps || 0) >= 7).length;
  const totalCompleted = instances.filter(i => i.StatusCode === 'COMPLETED').length;
  const totalHired = instances.filter(i => isCandidateHired(i)).length;
  const totalYetToHired = instances.filter(i => i.StatusCode === 'IN_PROGRESS' && !isCandidateHired(i)).length;

  return (
    <div className="onboarding-container">
      {/* Header */}
      <div className="onboarding-header">
        <div>
          <h1>Onboarding Management Workstation</h1>
          <p>Track, manage, and complete pre-employment and commercial onboarding checklists.</p>
        </div>
      </div>

      {/* Control Bar: Date filters, Client, Entity, Status, Search */}
      <div className="onboarding-controls">
        <div className="onboarding-controls-top">
          <span className="onboarding-filter-label">Date filters:</span>
        </div>

        <div className="onboarding-filters-row">
          <div className="onboarding-input-group">
            <label>Quick Select</label>
            <select 
              value={quickSelect} 
              onChange={handleQuickSelectChange}
              className="onboarding-filter-select"
            >
              <option value="All">All Dates</option>
              <option value="Today">Today</option>
              <option value="Yesterday">Yesterday</option>
              <option value="This Week">This Week</option>
              <option value="Last Week">Last Week</option>
              <option value="This Month">This Month</option>
              <option value="Last Month">Last Month</option>
              <option value="This Quarter">This Quarter</option>
              <option value="Last Quarter">Last Quarter</option>
              <option value="Custom">Custom</option>
            </select>
          </div>

          <div className="onboarding-input-group">
            <label>Start date</label>
            <input 
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setQuickSelect(e.target.value || endDate ? 'Custom' : 'All');
              }}
              className="onboarding-filter-date"
            />
          </div>

          <div className="onboarding-input-group">
            <label>End date</label>
            <input 
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setQuickSelect(startDate || e.target.value ? 'Custom' : 'All');
              }}
              className="onboarding-filter-date"
            />
          </div>

          {(startDate || endDate) && (
            <button
              type="button"
              className="onboarding-clear-date-btn"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setQuickSelect('All');
              }}
              title="Clear date filter"
            >
              <X size={14} /> Clear
            </button>
          )}

          <div className="onboarding-input-group">
            <label>Client</label>
            <select 
              className="onboarding-filter-select"
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
            >
              <option value="">All Clients</option>
              {clientOptions.map((client) => (
                <option key={client} value={client}>{client}</option>
              ))}
            </select>
          </div>

          <div className="onboarding-input-group">
            <label>Supplier</label>
            <select 
              className="onboarding-filter-select"
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
            >
              <option value="">All Suppliers</option>
              {supplierOptions.map((supplier) => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
          </div>

          <div className="onboarding-input-group">
            <label>Entity</label>
            <select 
              className="onboarding-filter-select onboarding-filter-entity"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
            >
              <option value="">All Entities</option>
              <option value="4" title="US - Prophecy Consulting INC">US - Prophecy</option>
              <option value="5" title="Offshore - Prophecy Offshore">Offshore - Prophecy</option>
            </select>
          </div>

          <div className="onboarding-input-group">
            <label>Status</label>
            <select 
              className="onboarding-filter-select"
              value={activeKpiFilter || statusFilter}
              onChange={(e) => {
                const val = e.target.value;
                setStatusFilter(val);
                setActiveKpiFilter(val || null);
              }}
            >
              <option value="">All Statuses</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="NEAR_COMPLETION">Near Completion (7+ Steps)</option>
              <option value="COMPLETED">Completed</option>
              <option value="HIRED">Hired</option>
              <option value="YET_TO_HIRE">Yet to be Hired</option>
            </select>
          </div>

          <div className="onboarding-search-group">
            <label>Search</label>
            <div className="onboarding-search-box">
              <Search size={18} color="#94a3b8" />
              <input
                type="text"
                placeholder="Search candidate name, job title, client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards (All 5 Clickable with Active Selection) */}
      <div className="onboarding-kpi-grid">
        <div 
          className={`onboarding-kpi-card ${activeKpiFilter === 'IN_PROGRESS' ? 'active' : ''}`}
          onClick={() => {
            if (activeKpiFilter === 'IN_PROGRESS') {
              setActiveKpiFilter(null);
              setStatusFilter('');
            } else {
              setActiveKpiFilter('IN_PROGRESS');
              setStatusFilter('IN_PROGRESS');
            }
          }}
          title="Click to filter table by Active In-Progress"
        >
          <div className="onboarding-kpi-icon kpi-blue">
            <Users size={22} />
          </div>
          <div className="onboarding-kpi-info">
            <h3>{totalInProgress}</h3>
            <span>Active In-Progress</span>
          </div>
        </div>

        <div 
          className={`onboarding-kpi-card ${activeKpiFilter === 'NEAR_COMPLETION' ? 'active' : ''}`}
          onClick={() => {
            if (activeKpiFilter === 'NEAR_COMPLETION') {
              setActiveKpiFilter(null);
              setStatusFilter('');
            } else {
              setActiveKpiFilter('NEAR_COMPLETION');
              setStatusFilter('NEAR_COMPLETION');
            }
          }}
          title="Click to filter table by Near Completion (7+ Steps)"
        >
          <div className="onboarding-kpi-icon kpi-purple">
            <UserCheck size={22} />
          </div>
          <div className="onboarding-kpi-info">
            <h3>{readyToComplete}</h3>
            <span>Near Completion (7+ Steps)</span>
          </div>
        </div>

        <div 
          className={`onboarding-kpi-card ${activeKpiFilter === 'COMPLETED' ? 'active' : ''}`}
          onClick={() => {
            if (activeKpiFilter === 'COMPLETED') {
              setActiveKpiFilter(null);
              setStatusFilter('');
            } else {
              setActiveKpiFilter('COMPLETED');
              setStatusFilter('COMPLETED');
            }
          }}
          title="Click to filter table by Completed"
        >
          <div className="onboarding-kpi-icon kpi-green">
            <CheckCircle2 size={22} />
          </div>
          <div className="onboarding-kpi-info">
            <h3>{totalCompleted}</h3>
            <span>Completed</span>
          </div>
        </div>

        <div 
          className={`onboarding-kpi-card ${activeKpiFilter === 'HIRED' ? 'active' : ''}`}
          onClick={() => {
            if (activeKpiFilter === 'HIRED') {
              setActiveKpiFilter(null);
              setStatusFilter('');
            } else {
              setActiveKpiFilter('HIRED');
              setStatusFilter('HIRED');
            }
          }}
          title="Click to filter table by Hired Candidates"
        >
          <div className="onboarding-kpi-icon kpi-emerald">
            <Briefcase size={22} />
          </div>
          <div className="onboarding-kpi-info">
            <h3>{totalHired}</h3>
            <span>Hired</span>
          </div>
        </div>

        <div 
          className={`onboarding-kpi-card ${activeKpiFilter === 'YET_TO_HIRE' ? 'active' : ''}`}
          onClick={() => {
            if (activeKpiFilter === 'YET_TO_HIRE') {
              setActiveKpiFilter(null);
              setStatusFilter('');
            } else {
              setActiveKpiFilter('YET_TO_HIRE');
              setStatusFilter('YET_TO_HIRE');
            }
          }}
          title="Click to filter table by Candidates Yet to be Hired"
        >
          <div className="onboarding-kpi-icon kpi-amber">
            <Clock size={22} />
          </div>
          <div className="onboarding-kpi-info">
            <h3>{totalYetToHired}</h3>
            <span>Yet to Hired</span>
          </div>
        </div>
      </div>

      {/* Queue Data Table */}
      <div className="onboarding-table-card">
        <table className="onboarding-table">
          <thead>
            <tr>
              <th 
                className="onb-th-sortable"
                onClick={() => handleSort('initiated')}
                title="Click to sort by Initiated Date"
              >
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span>Initiated</span>
                  {sortConfig.key === 'initiated' ? (
                    sortConfig.direction === 'asc' ? <ArrowUp size={14} color="#3b5bfd" /> : <ArrowDown size={14} color="#3b5bfd" />
                  ) : (
                    <ArrowUpDown size={14} color="#94a3b8" />
                  )}
                </div>
              </th>
              <th>Candidate</th>
              <th>Job / Role</th>
              <th>Employment Type</th>
              <th>Client / Entity</th>
              <th 
                className="onb-th-sortable"
                onClick={() => handleSort('checklist')}
                title="Click to sort by Checklist Progress"
              >
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <span>Checklist Progress</span>
                  {sortConfig.key === 'checklist' ? (
                    sortConfig.direction === 'asc' ? <ArrowUp size={14} color="#3b5bfd" /> : <ArrowDown size={14} color="#3b5bfd" />
                  ) : (
                    <ArrowUpDown size={14} color="#94a3b8" />
                  )}
                </div>
              </th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  <Clock size={24} style={{ animation: 'spin 1.5s linear infinite', marginBottom: '8px' }} />
                  <p>Loading onboarding queue...</p>
                </td>
              </tr>
            ) : sortedInstances.length === 0 ? (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                  No onboarding records found matching current filters.
                </td>
              </tr>
            ) : (
              paginatedInstances.map((inst) => {
                const { totalSteps, completedSteps, percent } = getEffectiveSteps(inst);
                const isCompleted = inst.StatusCode === 'COMPLETED';

                return (
                  <tr key={inst.OnboardingInstanceId}>
                    <td>
                      <span style={{ fontWeight: 600, color: '#334155', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>
                        {formatOnboardDate(inst.InitiatedAtUtc || inst.CreatedAtUtc)}
                      </span>
                      {inst.InitiatedByUserName && (
                        <span style={{ fontSize: '0.74rem', color: '#64748b', display: 'block', marginTop: '2px' }}>
                          <strong style={{ color: '#475569' }}>Submitted by:</strong> {inst.InitiatedByUserName}
                        </span>
                      )}
                    </td>
                    <td className="candidate-cell">
                      <div 
                        style={{ 
                          fontWeight: 600, 
                          cursor: inst.CandidateId ? 'pointer' : 'default', 
                          color: inst.CandidateId ? '#0284c7' : '#0f172a',
                          display: 'inline-block'
                        }}
                        onClick={() => {
                          if (inst.CandidateId) setSelectedCandidateId(inst.CandidateId);
                        }}
                        title={inst.CandidateId ? "Click to view Candidate Quick Overview" : undefined}
                        onMouseEnter={(e) => { if (inst.CandidateId) e.currentTarget.style.textDecoration = 'underline'; }}
                        onMouseLeave={(e) => { if (inst.CandidateId) e.currentTarget.style.textDecoration = 'none'; }}
                      >
                        {inst.CandidateFirstName} {inst.CandidateLastName}
                      </div>
                      <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block' }}>
                        {inst.CandidateEmail || 'No email provided'}
                      </span>
                    </td>
                    <td>
                      <div 
                        style={{ 
                          fontWeight: 500, 
                          color: inst.JobId ? '#0284c7' : '#0f172a', 
                          cursor: inst.JobId ? 'pointer' : 'default',
                          display: 'inline-block'
                        }}
                        onClick={() => {
                          if (inst.JobId) setSelectedRoleId(inst.JobId);
                        }}
                        title={inst.JobId ? "Click to view Role Details" : undefined}
                        onMouseEnter={(e) => { if (inst.JobId) e.currentTarget.style.textDecoration = 'underline'; }}
                        onMouseLeave={(e) => { if (inst.JobId) e.currentTarget.style.textDecoration = 'none'; }}
                      >
                        {inst.JobTitle || 'N/A'}
                      </div>
                      <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block' }}>
                        Code: {inst.ExternalJobReference && inst.ExternalJobReference !== 'N/A' ? inst.ExternalJobReference : (inst.JobId ? `#${inst.JobId}` : 'N/A')}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          {(() => {
                            const currentEmpType = normalizeEmploymentType(inst.EmploymentType);
                            const isC2C = currentEmpType === 'C2C';
                            const hasVal = currentEmpType && currentEmpType !== 'N/A';
                            return (
                              <select
                                value={hasVal ? currentEmpType : ''}
                                disabled={updatingEmpTypeId === inst.OnboardingInstanceId}
                                onChange={(e) => handleEmploymentTypeChange(inst.OnboardingInstanceId, e.target.value, currentEmpType, inst.CandidateId)}
                                title="Click to edit Employment Type"
                                style={{
                                  fontSize: '0.78rem',
                                  fontWeight: 600,
                                  height: '32px',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  outline: 'none',
                                  transition: 'all 0.15s ease',
                                  background: isC2C ? '#fef3c7' : (hasVal ? '#eff6ff' : '#f8fafc'),
                                  color: isC2C ? '#92400e' : (hasVal ? '#166534' : '#475569'),
                                  border: isC2C ? '1px solid #fde68a' : (hasVal ? '1px solid #bbf7d0' : '1px solid #cbd5e1'),
                                  minWidth: '130px',
                                  maxWidth: '160px',
                                  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
                                }}
                              >
                                <option value="" disabled>Select.....</option>
                                {EMPLOYMENT_TYPE_OPTIONS.map(opt => (
                                  <option key={opt} value={opt} style={{ background: '#ffffff', color: '#1e293b' }}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}
                        </div>
                        {inst.ContractorCompanyName && (
                          <span 
                            style={{ 
                              fontSize: '0.74rem', 
                              color: '#475569', 
                              fontWeight: 500, 
                              display: 'inline-flex', 
                              alignItems: 'center', 
                              gap: '4px' 
                            }} 
                            title="Contractor / Supplier Company"
                          >
                            <Building2 size={12} style={{ flexShrink: 0, color: '#64748b' }} />
                            {inst.ContractorCompanyName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>
                        {inst.ClientCompanyName || 'Direct'}
                      </div>
                      <span style={{ fontSize: '0.76rem', color: '#64748b' }}>
                        Entity: {inst.EntityName || 'Prophecy Consulting INC'}
                      </span>
                    </td>
                    <td>
                      <div className="onb-progress-container">
                        <div className="onb-progress-label">
                          <span>{completedSteps}/{totalSteps} steps</span>
                          <span>{percent}%</span>
                        </div>
                        <div className="onb-progress-track">
                          <div 
                            className="onb-progress-fill" 
                            style={{ width: `${percent}%` }}
                          ></div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span 
                        className={`onb-status-chip ${isCompleted ? 'completed' : 'in-progress'}`}
                        style={{ cursor: !isCompleted && inst.pendingSteps?.length > 0 ? 'help' : 'default' }}
                        title={!isCompleted && inst.pendingSteps?.length > 0 ? `Pending Steps (${inst.pendingSteps.length}):\n• ${inst.pendingSteps.join('\n• ')}` : ''}
                      >
                        {inst.StatusCode}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="onb-btn-primary"
                        onClick={() => setSelectedInstanceId(inst.OnboardingInstanceId)}
                      >
                        Checklist <ChevronRight size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination Controls */}
        {sortedInstances.length > 0 && (
          <div className="onb-pagination">
            <div className="onb-pagination-info">
              <span>
                Showing <strong>{totalItems === 0 ? 0 : startIndex + 1}</strong> to <strong>{endIndex}</strong> of <strong>{totalItems}</strong> candidates
              </span>
              <div className="onb-page-size-selector">
                <span>Rows per page:</span>
                <select 
                  value={pageSize} 
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="onb-pagination-controls">
              <button 
                className="onb-page-btn" 
                onClick={() => setCurrentPage(1)} 
                disabled={currentPage === 1}
                title="First Page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button 
                className="onb-page-btn" 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                disabled={currentPage === 1}
                title="Previous Page"
              >
                <ChevronLeft size={16} />
              </button>

              {getPageNumbers(currentPage, totalPages).map((p, idx) => (
                p === '...' ? (
                  <span key={`dots-${idx}`} className="onb-page-dots">...</span>
                ) : (
                  <button
                    key={`page-${p}`}
                    className={`onb-page-btn ${currentPage === p ? 'active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              ))}

              <button 
                className="onb-page-btn" 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                disabled={currentPage === totalPages}
                title="Next Page"
              >
                <ChevronRight size={16} />
              </button>
              <button 
                className="onb-page-btn" 
                onClick={() => setCurrentPage(totalPages)} 
                disabled={currentPage === totalPages}
                title="Last Page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Onboarding Detail Drawer Workstation */}
      {selectedInstanceId && (
        <OnboardingDetailDrawer
          instanceId={selectedInstanceId}
          onClose={() => setSelectedInstanceId(null)}
          onUpdated={fetchQueue}
        />
      )}

      {/* Candidate Quick Overview Modal */}
      {selectedCandidateId && (
        <CandidateDetail
          candidateId={selectedCandidateId}
          isModal={true}
          onClose={() => {
            setSelectedCandidateId(null);
            fetchQueue();
          }}
        />
      )}

      {/* Role Detail View Modal */}
      {selectedRoleId && (
        <RoleDetailView
          roleId={selectedRoleId}
          isModal={true}
          onClose={() => setSelectedRoleId(null)}
        />
      )}
    </div>
  );
};

export default OnboardingQueue;
